import { useState, useEffect, useCallback } from 'react';
import './RadarTab.css';
import { supabase } from './supabase';
import { useEffectiveUser } from './ImpersonationContext';

const VPS = 'https://copyccxx.com';

function timeAgo(isoTs) {
  if (!isoTs) return '—';
  const diff = Math.floor((Date.now() - new Date(isoTs).getTime()) / 1000);
  if (diff < 60)    return 'il y a quelques secondes';
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)} jours`;
}

const truncAddr = a => `${a.slice(0, 6)}…${a.slice(-4)}`;

const DEFAULT_SETTINGS = {
  enabled:                  false,
  min_trade_amount_sol:     1.0,
  max_pages_wallet_scoring: 5,
  max_wallets:              500,
  min_score_threshold:      45,
};

function activityBadge(ts) {
  if (!ts) return null;
  const h = (Date.now() - new Date(ts).getTime()) / 3600000;
  if (h < 24) return { label: timeAgo(ts), color: '#00c864', bg: 'rgba(0,200,100,0.12)', border: '1px solid rgba(0,200,100,0.28)' };
  if (h < 72) return { label: timeAgo(ts), color: '#f0a500', bg: 'rgba(240,165,0,0.12)', border: '1px solid rgba(240,165,0,0.28)' };
  return           { label: timeAgo(ts), color: '#5a6a80', bg: 'rgba(90,106,128,0.12)', border: '1px solid rgba(90,106,128,0.28)' };
}

function formatHold(m) {
  if (m == null) return '—';
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60), rem = m % 60;
  if (h < 24) return rem > 0 ? `${h}h${String(rem).padStart(2, '0')}` : `${h}h`;
  return `${Math.round(m / 1440)}j`;
}

const DISCOVERY_SOURCE_LABELS = {
  jup_ray:          'Jupiter/Raydium',
  gecko_new_pools:  'GeckoTerminal (legacy)',
  pumpfun:          'Pump.fun (legacy)',
  gt_trending:      'GT Trending (legacy)',
  scan:             'Scan (legacy)',
};

function getCycleKeyUI(createdAt) {
  if (!createdAt) { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; }
  const origin=new Date(createdAt); origin.setHours(0,0,0,0);
  const now=new Date(); now.setHours(0,0,0,0);
  const n=Math.floor(Math.floor((now-origin)/86400000)/30);
  const s=new Date(origin.getTime()+n*30*86400000);
  return `${s.getFullYear()}-${String(s.getMonth()+1).padStart(2,'0')}-${String(s.getDate()).padStart(2,'0')}`;
}
function getCycleDaysLeftUI(createdAt) {
  if (!createdAt) { const n=new Date(); return Math.max(1,new Date(n.getFullYear(),n.getMonth()+1,0).getDate()-n.getDate()+1); }
  const origin=new Date(createdAt); origin.setHours(0,0,0,0);
  const now=new Date(); now.setHours(0,0,0,0);
  const n=Math.floor(Math.floor((now-origin)/86400000)/30);
  const end=new Date(origin.getTime()+(n+1)*30*86400000);
  return Math.max(1,Math.floor((end-now)/86400000));
}

export default function RadarTab({ onCopyWallet }) {
  const { effectiveUserId, isImpersonating } = useEffectiveUser();

  const [wallets,        setWallets]        = useState([]);
  const [settings,       setSettings]       = useState(null);
  const [lastScan,       setLastScan]       = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [settingsOpen,   setSettingsOpen]   = useState(false);
  const [settingsForm,   setSettingsForm]   = useState(DEFAULT_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);
  const [rescoringMsg,   setRescoringMsg]   = useState(null);
  const [confirmRescore, setConfirmRescore] = useState(null);
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [copiedId,       setCopiedId]       = useState(null);
  const [sortKey,        setSortKey]        = useState('follow_score');
  const [sortDir,        setSortDir]        = useState('desc');
  const [activeKeys,     setActiveKeys]     = useState(0);
  const [budgetDaily,    setBudgetDaily]    = useState(null);
  const [selectedRadarWallet, setSelectedRadarWallet] = useState(null);
  const [compactMode,    setCompactMode]    = useState(false);
  const [favLoading,     setFavLoading]     = useState(new Set());
  const [searchQuery,    setSearchQuery]    = useState('');

  const loadAll = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = effectiveUserId || session?.user?.id;
    if (!userId) { setLoading(false); return; }

    const [{ data: ws }, { data: st }, { data: log }, rotRes, activeKeyRes] = await Promise.all([
      supabase.from('radar_wallets')
        .select('*')
        .eq('user_id', userId)
        .order('follow_score',  { ascending: false, nullsFirst: false })
        .order('discovered_at', { ascending: false }),
      supabase.from('radar_settings')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(),
      supabase.from('radar_scan_log')
        .select('scanned_at, tokens_scanned, wallets_added, wallets_evicted')
        .eq('user_id', userId)
        .eq('status', 'success')
        .order('scanned_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase.from('helius_keys').select('id', { count: 'exact', head: true }).eq('in_rotation', true).is('blocked_at', null),
      supabase.from('helius_keys').select('created_at').eq('is_active', true).limit(1).maybeSingle(),
    ]);

    setWallets((ws || []).map(w => ({
      ...w,
      win_rate:         w.win_rate         ?? w.score_data?.winRate         ?? null,
      overall_roi:      w.overall_roi      ?? w.score_data?.overallRoi      ?? null,
      best_trade_roi:   w.best_trade_roi   ?? w.score_data?.bestTradeRoi    ?? null,
      total_trades:     w.total_trades     ?? w.score_data?.totalTrades     ?? null,
      avg_hold_minutes: w.avg_hold_minutes ?? w.score_data?.avgHoldMinutes  ?? null,
      regularity_weeks: w.regularity_weeks ?? w.score_data?.regularityWeeks ?? null,
    })));
    setSettings(st || null);
    setSettingsForm(st ? { ...DEFAULT_SETTINGS, ...st } : DEFAULT_SETTINGS);
    setLastScan(log || null);

    const activeCreatedAt  = activeKeyRes?.data?.created_at || null;
    const cycleKey         = getCycleKeyUI(activeCreatedAt);
    const { data: budgetRow } = await supabase.from('radar_budget').select('credits_used').eq('month', cycleKey).maybeSingle();
    const rotCount         = rotRes?.count || 0;
    const budgetTotal      = rotCount * 1_000_000;
    const creditsUsed      = budgetRow?.credits_used || 0;
    const creditsRemaining = Math.max(0, budgetTotal - creditsUsed);
    const daysLeft         = getCycleDaysLeftUI(activeCreatedAt);
    setActiveKeys(rotCount);
    setBudgetDaily(Math.round(creditsRemaining / daysLeft));

    setLoading(false);
  }, [effectiveUserId]);

  useEffect(() => {
    setLoading(true);
    loadAll();
    let sub = null, budgetSub = null;
    supabase.auth.getSession().then(({ data: { session } }) => {
      const userId = effectiveUserId || session?.user?.id;
      if (!userId) return;
      sub = supabase.channel(`radar-wallets-${userId}`)
        .on('postgres_changes',
          { event: '*', schema: 'public', table: 'radar_wallets', filter: `user_id=eq.${userId}` },
          loadAll)
        .subscribe();
      budgetSub = supabase.channel('radar-budget-rt')
        .on('postgres_changes', { event: '*',      schema: 'public', table: 'radar_budget' }, loadAll)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'helius_keys'  }, loadAll)
        .subscribe();
    });
    return () => { sub?.unsubscribe(); budgetSub?.unsubscribe(); };
  }, [effectiveUserId, loadAll]);

  const handleToggle = async () => {
    if (isImpersonating) return;
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;
    const newEnabled = !(settings?.enabled ?? false);
    await supabase.from('radar_settings').upsert(
      { user_id: userId, ...DEFAULT_SETTINGS, ...(settings || {}), enabled: newEnabled },
      { onConflict: 'user_id' }
    );
    setSettings(s => ({ ...DEFAULT_SETTINGS, ...(s || {}), enabled: newEnabled }));
  };

  const handleDeleteWallet = async (wallet) => {
    if (isImpersonating) return;
    if (!window.confirm(`Supprimer ${truncAddr(wallet.wallet_address)} du Radar ?`)) return;
    setWallets(prev => prev.filter(w => w.id !== wallet.id));
    await supabase.from('radar_wallets').delete().eq('id', wallet.id);
  };

  const RESCORE_WARN_THRESHOLD = 50;

  const doRescore = async () => {
    setConfirmRescore(null);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    setRescoringMsg(null);
    try {
      const r = await fetch(`${VPS}/api/radar/rescore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      });
      if (r.ok) {
        setRescoringMsg('Rescoring lancé — résultats disponibles sous quelques minutes.');
        setTimeout(() => setRescoringMsg(null), 8000);
      } else {
        const e = await r.json().catch(() => ({}));
        setRescoringMsg(`Erreur : ${e.error || r.status}`);
      }
    } catch (e) {
      setRescoringMsg(`Erreur réseau : ${e.message}`);
    }
  };

  const handleRescore = () => {
    if (isImpersonating) return;
    const cacheHours = settings?.cache_duration_hours ?? 24;
    const toRescore = wallets.filter(w => {
      if (!w.last_score_at) return true;
      return (Date.now() - new Date(w.last_score_at).getTime()) / 3600000 >= cacheHours;
    });
    if (toRescore.length > RESCORE_WARN_THRESHOLD) {
      setConfirmRescore({ count: toRescore.length, creditEst: Math.round(toRescore.length * 990) });
      return;
    }
    doRescore();
  };

  const handleSaveSettings = async () => {
    if (isImpersonating) return;
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;
    setSavingSettings(true);
    const payload = {
      user_id:                  userId,
      enabled:                  settings?.enabled ?? false,
      min_trade_amount_sol:     Number(settingsForm.min_trade_amount_sol),
      max_pages_wallet_scoring: Number(settingsForm.max_pages_wallet_scoring),
      max_wallets:              Number(settingsForm.max_wallets),
      min_score_threshold:      Number(settingsForm.min_score_threshold),
    };
    const { data } = await supabase.from('radar_settings')
      .upsert(payload, { onConflict: 'user_id' })
      .select()
      .maybeSingle();
    if (data) setSettings(data);
    setSavingSettings(false);
  };

  const handleToggleFavorite = async (e, wallet) => {
    e.stopPropagation();
    if (isImpersonating) return;
    const newVal = !wallet.is_favorite;
    setFavLoading(prev => new Set([...prev, wallet.id]));
    setWallets(prev => prev.map(w => w.id === wallet.id ? { ...w, is_favorite: newVal } : w));
    await supabase.from('radar_wallets').update({ is_favorite: newVal }).eq('id', wallet.id);
    setFavLoading(prev => { const s = new Set(prev); s.delete(wallet.id); return s; });
  };

  const handleCopy = (w) => {
    navigator.clipboard.writeText(w.wallet_address);
    setCopiedId(w.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const exportCSV = () => {
    const headers = ['Adresse', 'Score', 'Recommandation', 'Dernière activité', 'Découvert le'];
    const rows = wallets.map(w => [
      w.wallet_address,
      w.follow_score ?? '',
      w.follow_recommendation ?? '',
      w.last_active_at ? new Date(w.last_active_at).toLocaleString('fr-FR') : '',
      w.discovered_at  ? new Date(w.discovered_at).toLocaleString('fr-FR')  : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `radar_wallets_${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const enabled        = settings?.enabled ?? false;
  const cutoff24h      = Date.now() - 24 * 3600 * 1000;
  const score60Count   = wallets.filter(w => (w.follow_score ?? 0) > 60).length;
  const holding1hCount = wallets.filter(w => (w.avg_hold_minutes ?? 0) > 60).length;
  const actifs24hCount = wallets.filter(w => w.last_active_at && new Date(w.last_active_at).getTime() >= cutoff24h).length;
  const favoriteCount  = wallets.filter(w => w.is_favorite).length;

  const visibleWallets = (() => {
    let r = wallets;
    if (filterStatus === 'score60')   r = r.filter(w => (w.follow_score ?? 0) > 60);
    if (filterStatus === 'holding1h') r = r.filter(w => (w.avg_hold_minutes ?? 0) > 60);
    if (filterStatus === 'actifs24h') r = r.filter(w => w.last_active_at && new Date(w.last_active_at).getTime() >= cutoff24h);
    if (filterStatus === 'favorites') r = r.filter(w => w.is_favorite);
    if (searchQuery.trim()) r = r.filter(w => w.wallet_address.toLowerCase().includes(searchQuery.toLowerCase().trim()));
    return r;
  })();

  const sortedWallets = [...visibleWallets].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string') return sortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  if (loading) {
    return <div className="rd-root"><div className="rd-loading">Chargement du Radar…</div></div>;
  }

  return (
    <div className="rd-root">

      {/* ── En-tête ── */}
      <div className="rd-header">
        <div className="rd-header-left">
          <button
            className={`rd-toggle ${enabled ? 'rd-toggle--on' : 'rd-toggle--off'}`}
            onClick={handleToggle}
            disabled={isImpersonating}
            title={enabled ? 'Désactiver le Radar' : 'Activer le Radar'}
          >
            <span className="rd-toggle-thumb" />
          </button>
          <span className="rd-toggle-label">
            Radar <span className={enabled ? 'rd-on' : 'rd-off'}>{enabled ? 'ACTIF' : 'DÉSACTIVÉ'}</span>
          </span>
          {lastScan
            ? <span className="rd-lastscan">· Dernier scan {timeAgo(lastScan.scanned_at)}</span>
            : <span className="rd-lastscan">· Aucun scan effectué</span>}
          {budgetDaily != null && (
            <span style={{fontSize:11,color:'#5a6a80'}}>
              · {activeKeys} clé{activeKeys !== 1 ? 's' : ''} · {budgetDaily.toLocaleString()} cr/j
            </span>
          )}
        </div>
        <div className="rd-header-right">
          <button
            className="rd-btn rd-btn--ghost"
            onClick={exportCSV}
            disabled={wallets.length === 0}
            title="Exporter en CSV"
          >⬇ CSV</button>
          <button className="rd-btn rd-btn--ghost" onClick={() => setSettingsOpen(o => !o)}>
            ⚙ Réglages
          </button>
          <button
            className="rd-btn rd-btn--accent"
            onClick={handleRescore}
            disabled={isImpersonating || wallets.length === 0}
            title={wallets.length === 0 ? 'Aucun wallet à scorer' : ''}
          >⟳ Rescore</button>
        </div>
      </div>

      {/* ── Feedback rescore ── */}
      {rescoringMsg && (
        <div className="rd-banner rd-banner--teal">{rescoringMsg}</div>
      )}
      {confirmRescore && (
        <div className="rd-banner rd-banner--warning" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
          <span>⚠ <b>{confirmRescore.count} wallets</b> à rescorer (~<b>{confirmRescore.creditEst.toLocaleString()}</b> crédits estimés). Confirmer ?</span>
          <div style={{display:'flex',gap:8,flexShrink:0}}>
            <button onClick={doRescore} style={{padding:'4px 14px',borderRadius:5,background:'#f59e0b',border:'none',color:'#000',fontWeight:700,cursor:'pointer',fontSize:11}}>Confirmer</button>
            <button onClick={() => setConfirmRescore(null)} style={{padding:'4px 14px',borderRadius:5,background:'transparent',border:'1px solid #5a6a80',color:'#5a6a80',fontWeight:700,cursor:'pointer',fontSize:11}}>Annuler</button>
          </div>
        </div>
      )}

      {/* ── Banner radar désactivé ── */}
      {!enabled && (
        <div className="rd-banner rd-banner--warning">
          Radar désactivé — aucun scan automatique ne sera effectué. Active le toggle pour démarrer la découverte.
        </div>
      )}

      {/* ── Panneau réglages ── */}
      {settingsOpen && (
        <div className="rd-settings-panel">
          <div className="rd-settings-title">Paramètres du scan</div>
          <div className="rd-settings-grid">
            {[
              { key: 'min_trade_amount_sol',    label: 'Mise min. par trade',  unit: 'SOL' },
              { key: 'max_pages_wallet_scoring', label: 'Pages de scoring',     unit: 'pages' },
              { key: 'max_wallets',             label: 'Wallets max en base',  unit: '' },
              { key: 'min_score_threshold',     label: 'Score min. insertion', unit: '/100' },
            ].map(({ key, label, unit }) => (
              <label key={key} className="rd-setting-field">
                <span className="rd-setting-label">{label}</span>
                <div className="rd-setting-input-wrap">
                  <input
                    type="number"
                    className="rd-setting-input"
                    value={settingsForm[key] ?? ''}
                    onChange={e => setSettingsForm(f => ({ ...f, [key]: e.target.value }))}
                    disabled={isImpersonating}
                  />
                  {unit && <span className="rd-setting-unit">{unit}</span>}
                </div>
              </label>
            ))}
            <div className="rd-setting-field">
              <span className="rd-setting-label">Intervalle scan (adaptatif)</span>
              <div className="rd-setting-input-wrap">
                <input
                  className="rd-setting-input"
                  readOnly
                  value={budgetDaily != null
                    ? `${Math.max(2, Math.round(4 - Math.min(1, (budgetDaily - 33333) / 1216667) * 2))}h`
                    : '—'}
                />
                <span className="rd-setting-unit">automatique</span>
              </div>
            </div>
          </div>
          <button
            className="rd-btn rd-btn--save"
            onClick={handleSaveSettings}
            disabled={savingSettings || isImpersonating}
          >
            {savingSettings ? 'Sauvegarde…' : 'Sauvegarder les réglages'}
          </button>
        </div>
      )}

      {/* ── Barre de filtres + tri + recherche ── */}
      <div className="rd-filter-bar">
        {[
          { key: 'all',        label: 'Tous',       count: wallets.length },
          { key: 'score60',    label: 'Score > 60', count: score60Count },
          { key: 'holding1h',  label: 'Holding > 1h', count: holding1hCount },
          { key: 'actifs24h',  label: 'Actifs < 24h', count: actifs24hCount },
          { key: 'favorites',  label: '⭐ Favoris', count: favoriteCount },
        ].map(({ key, label, count }) => (
          <button
            key={key}
            className={`rd-filter-btn${filterStatus === key ? ' rd-filter-btn--active' : ''}`}
            onClick={() => setFilterStatus(key)}
          >
            {label}<span className="rd-filter-count">{count}</span>
          </button>
        ))}
        <div style={{marginLeft:'auto',display:'flex',gap:4,alignItems:'center'}}>
          {[
            { key: 'follow_score',   label: 'Score' },
            { key: 'avg_hold_minutes', label: 'Holding' },
            { key: 'last_active_at', label: 'Activité' },
            { key: 'discovered_at',  label: 'Découverte' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`rd-filter-btn${sortKey === key ? ' rd-filter-btn--active' : ''}`}
              onClick={() => handleSort(key)}
            >
              {label}<span className="rd-sort-icon">{sortKey === key ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span>
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Rechercher adresse…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{background:'var(--bg-3)',border:'1px solid var(--border)',borderRadius:6,
                  color:'var(--text-1)',fontSize:11,padding:'4px 10px',
                  fontFamily:'var(--font-mono)',width:160,outline:'none'}}
        />
      </div>

      {/* ── Tableau wallets ── */}
      {wallets.length === 0 ? (
        <div className="rd-empty">
          {enabled
            ? 'Aucun wallet découvert pour l\'instant — le scanner tourne en arrière-plan.'
            : 'Active le Radar pour commencer la découverte automatique de wallets.'}
        </div>
      ) : visibleWallets.length === 0 ? (
        <div className="rd-empty rd-empty--filtered">Aucun wallet dans cette catégorie.</div>
      ) : (
        <div className={`rd-table-wrap${!enabled ? ' rd-table-wrap--dimmed' : ''}`}>
          <table className={`rd-table${compactMode ? ' rd-table--compact' : ''}`}>
            <thead>
              <tr>
                <th style={{width:32}}></th>
                <th
                  className={`rd-th-sort${sortKey === 'discovered_at' ? ' rd-th-sort--active' : ''}`}
                  onClick={() => handleSort('discovered_at')}
                >Wallet<span className="rd-sort-icon">{sortKey === 'discovered_at' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span></th>
                <th
                  className={`rd-th-sort${sortKey === 'follow_score' ? ' rd-th-sort--active' : ''}`}
                  onClick={() => handleSort('follow_score')}
                >Score<span className="rd-sort-icon">{sortKey === 'follow_score' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span></th>
                <th
                  className={`rd-th-sort${sortKey === 'avg_hold_minutes' ? ' rd-th-sort--active' : ''}`}
                  onClick={() => handleSort('avg_hold_minutes')}
                  title="Durée moyenne de détention — copiabilité"
                >Holding<span className="rd-sort-icon">{sortKey === 'avg_hold_minutes' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span></th>
                <th
                  className={`rd-th-sort${sortKey === 'last_active_at' ? ' rd-th-sort--active' : ''}`}
                  onClick={() => handleSort('last_active_at')}
                >Dernière activité<span className="rd-sort-icon">{sortKey === 'last_active_at' ? (sortDir === 'desc' ? '↓' : '↑') : '↕'}</span></th>
                <th>Découvert</th>
                <th className={`rd-th-sort${sortKey==='win_rate'?' rd-th-sort--active':''}`} onClick={()=>handleSort('win_rate')}>Win Rate<span className="rd-sort-icon">{sortKey==='win_rate'?(sortDir==='desc'?'↓':'↑'):'↕'}</span></th>
                <th className={`rd-th-sort${sortKey==='overall_roi'?' rd-th-sort--active':''}`} onClick={()=>handleSort('overall_roi')}>ROI moy.<span className="rd-sort-icon">{sortKey==='overall_roi'?(sortDir==='desc'?'↓':'↑'):'↕'}</span></th>
                <th className={`rd-th-sort${sortKey==='best_trade_roi'?' rd-th-sort--active':''}`} onClick={()=>handleSort('best_trade_roi')}>Best<span className="rd-sort-icon">{sortKey==='best_trade_roi'?(sortDir==='desc'?'↓':'↑'):'↕'}</span></th>
                <th className={`rd-th-sort${sortKey==='total_volume_usd'?' rd-th-sort--active':''}`} onClick={()=>handleSort('total_volume_usd')}>Volume<span className="rd-sort-icon">{sortKey==='total_volume_usd'?(sortDir==='desc'?'↓':'↑'):'↕'}</span></th>
                <th className={`rd-th-sort${sortKey==='total_trades'?' rd-th-sort--active':''}`} onClick={()=>handleSort('total_trades')}>Trades<span className="rd-sort-icon">{sortKey==='total_trades'?(sortDir==='desc'?'↓':'↑'):'↕'}</span></th>
                <th>Source</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedWallets.map(w => {
                const badge = activityBadge(w.last_active_at);
                return (
                  <tr key={w.id} className="rd-row" onClick={() => setSelectedRadarWallet(w)} style={{cursor:'pointer'}}>
                    {/* Favori */}
                    <td style={{width:32}}>
                      <button
                        className="rd-del-btn"
                        onClick={e => handleToggleFavorite(e, w)}
                        disabled={favLoading.has(w.id) || isImpersonating}
                        title={w.is_favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                        style={{opacity:favLoading.has(w.id)?0.4:1,background:'transparent',
                                borderColor:'transparent',color:w.is_favorite?'#f0a500':'#3a4a5a'}}
                      >{w.is_favorite ? '⭐' : '☆'}</button>
                    </td>
                    {/* Wallet */}
                    <td className="rd-cell-addr">
                      <button
                        className="rd-addr-copy"
                        onClick={e => { e.stopPropagation(); handleCopy(w); }}
                        title={copiedId === w.id ? 'Copié !' : "Copier l'adresse"}
                      >
                        {copiedId === w.id ? '✓ Copié' : truncAddr(w.wallet_address)}
                      </button>
                      <a
                        href={`https://solscan.io/account/${w.wallet_address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rd-addr-ext"
                        title="Ouvrir dans Solscan"
                        onClick={e => e.stopPropagation()}
                      >↗</a>
                    </td>
                    {/* Score */}
                    <td>
                      {w.follow_score != null ? (
                        <span className="rd-follow-wrap">
                          <span className={`rd-follow-score rd-follow-score--${
                            w.follow_recommendation === 'Suivre'        ? 'green'
                          : w.follow_recommendation === 'Ne pas suivre' ? 'red'
                          : 'orange'}`}>
                            {w.follow_score}
                          </span>
                          <span className="rd-follow-rec">{w.follow_recommendation}</span>
                        </span>
                      ) : (
                        <span className="rd-not-scored">Non scoré</span>
                      )}
                    </td>
                    {/* Holding — durée moyenne de détention, critère de copiabilité */}
                    <td className="rd-cell-num" style={{color:w.avg_hold_minutes==null?'#3a4a5a':w.avg_hold_minutes>=60?'#00e5cc':'#f0a500',fontWeight:700}}>
                      {formatHold(w.avg_hold_minutes)}
                    </td>
                    {/* Dernière activité */}
                    <td>
                      {badge
                        ? <span style={{fontSize:11,fontWeight:600,padding:'2px 7px',borderRadius:4,
                                        background:badge.bg,border:badge.border,color:badge.color,
                                        whiteSpace:'nowrap'}}>{badge.label}</span>
                        : <span style={{color:'#3a4a5a',fontSize:11}}>—</span>
                      }
                    </td>
                    {/* Découvert */}
                    <td style={{fontSize:11,color:'#5a6a80'}}>{timeAgo(w.discovered_at)}</td>
                    {/* Win Rate */}
                    <td className="rd-cell-num" style={{color:w.win_rate==null?'#3a4a5a':w.win_rate>=60?'#00e5cc':w.win_rate>=40?'#f0a500':'#ff6b6b',fontWeight:700}}>
                      {w.win_rate!=null?`${Math.round(w.win_rate)}%`:'—'}
                    </td>
                    {/* ROI moyen */}
                    <td className="rd-cell-num" style={{color:w.overall_roi==null?'#3a4a5a':w.overall_roi>=0?'#00e5cc':'#ff6b6b'}}>
                      {w.overall_roi!=null?`${w.overall_roi>=0?'+':''}${w.overall_roi.toFixed(1)}%`:'—'}
                    </td>
                    {/* Meilleur trade */}
                    <td className="rd-cell-num" style={{color:w.best_trade_roi==null?'#3a4a5a':'#f0a500'}}>
                      {w.best_trade_roi!=null?`🏆 +${Math.round(w.best_trade_roi)}%`:'—'}
                    </td>
                    {/* Volume total */}
                    <td className="rd-cell-num" style={{color:'#c0c8d8'}}>
                      {w.total_volume_usd!=null
                        ?w.total_volume_usd>=1000?`$${(w.total_volume_usd/1000).toFixed(1)}K`:`$${Math.round(w.total_volume_usd)}`
                        :'—'}
                    </td>
                    {/* Nb trades */}
                    <td className="rd-cell-num" style={{color:w.total_trades==null?'#3a4a5a':w.total_trades>=15?'#c0c8d8':'#5a6a80'}}>
                      {w.total_trades!=null?`${w.total_trades}`:'—'}
                    </td>
                    {/* Source de découverte */}
                    <td style={{fontSize:11,color:'#5a6a80',whiteSpace:'nowrap'}}>
                      {DISCOVERY_SOURCE_LABELS[w.discovery_source] ?? w.discovery_source ?? '—'}
                    </td>
                    {/* Actions */}
                    <td className="rd-cell-del">
                      {onCopyWallet && (
                        <button
                          className="rd-del-btn"
                          onClick={e => { e.stopPropagation(); onCopyWallet(w); }}
                          title="Créer une config Copytrade"
                          style={{background:'rgba(0,229,204,0.15)',borderColor:'rgba(0,229,204,0.2)',color:'#00e5cc'}}
                        >+</button>
                      )}
                      <button
                        className="rd-del-btn"
                        onClick={e => { e.stopPropagation(); handleDeleteWallet(w); }}
                        disabled={isImpersonating}
                        title="Supprimer ce wallet du Radar"
                      >✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal détail wallet ── */}
      {selectedRadarWallet && (() => {
        const w = selectedRadarWallet;
        const b = activityBadge(w.last_active_at);
        return (
          <div
            style={{position:'fixed',inset:0,zIndex:1000,background:'rgba(0,0,0,0.85)',
                    backdropFilter:'blur(8px)',display:'flex',alignItems:'center',
                    justifyContent:'center',padding:16}}
            onClick={() => setSelectedRadarWallet(null)}
          >
            <div
              style={{background:'#0d1018',border:'1px solid #1a2030',borderRadius:12,
                      padding:24,maxWidth:480,width:'100%',maxHeight:'90vh',overflowY:'auto'}}
              onClick={e => e.stopPropagation()}
            >
              {/* En-tête */}
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:16}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:10,color:'#5a6a80',marginBottom:4,fontWeight:600,letterSpacing:'0.8px'}}>WALLET RADAR</div>
                  <div style={{fontFamily:'monospace',fontSize:11,color:'#c0c8d8',wordBreak:'break-all',marginBottom:8}}>{w.wallet_address}</div>
                </div>
                <button
                  onClick={() => setSelectedRadarWallet(null)}
                  style={{background:'none',border:'none',cursor:'pointer',color:'#5a6a80',fontSize:20,padding:'0 4px',lineHeight:1,flexShrink:0}}
                >✕</button>
              </div>
              {/* Actions */}
              <div style={{display:'flex',gap:8,marginBottom:16}}>
                <button
                  onClick={() => navigator.clipboard.writeText(w.wallet_address)}
                  style={{flex:1,padding:'7px 12px',borderRadius:6,background:'rgba(0,229,204,0.1)',border:'1px solid rgba(0,229,204,0.2)',color:'#00e5cc',fontSize:11,fontWeight:700,cursor:'pointer'}}
                >Copier l'adresse</button>
                <a
                  href={`https://solscan.io/account/${w.wallet_address}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{padding:'7px 12px',borderRadius:6,background:'rgba(0,229,204,0.15)',border:'1px solid rgba(0,229,204,0.2)',color:'#00e5cc',fontSize:11,fontWeight:700,textDecoration:'none'}}
                >Solscan ↗</a>
                {onCopyWallet && (
                  <button
                    onClick={() => { setSelectedRadarWallet(null); onCopyWallet(w); }}
                    style={{padding:'7px 12px',borderRadius:6,background:'rgba(0,229,204,0.15)',border:'1px solid rgba(0,229,204,0.2)',color:'#00e5cc',fontSize:11,fontWeight:700,cursor:'pointer'}}
                  >+ Config</button>
                )}
              </div>
              {/* Grille stats */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
                {[
                  ['Dernière activité', b ? b.label : '—'],
                  ['Score fiabilité',   w.follow_score ?? '—'],
                  ['Découvert',         timeAgo(w.discovered_at)],
                  ['Scoré le',          w.last_score_at ? timeAgo(w.last_score_at) : '—'],
                  ['Hold moyen', formatHold(w.avg_hold_minutes)],
                  ['Régularité 30j', w.regularity_weeks != null
                    ? `${w.regularity_weeks}/4 sem. actives`
                    : '—'],
                ].map(([label, val]) => (
                  <div key={label} style={{background:'#080d14',border:'1px solid #1a2030',borderRadius:8,padding:'10px 12px'}}>
                    <div style={{fontSize:10,color:'#5a6a80',marginBottom:3}}>{label}</div>
                    <div style={{fontSize:14,fontWeight:700,color:'#c0c8d8'}}>{String(val)}</div>
                  </div>
                ))}
              </div>
              {/* Recommandation */}
              {w.follow_score != null && (
                <div style={{background:'#080d14',border:'1px solid #1a2030',borderRadius:8,padding:'12px 14px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:11,fontWeight:700,color:'#c0c8d8'}}>Recommandation</span>
                    <span style={{fontSize:11,fontWeight:700,color:
                      w.follow_recommendation === 'Suivre'        ? '#00e5cc'
                    : w.follow_recommendation === 'Ne pas suivre' ? '#ff6b6b'
                    : '#f0a500'}}>
                      {w.follow_recommendation || '—'}
                    </span>
                  </div>
                  {w.last_score_at && (
                    <div style={{fontSize:10,color:'#3a4a5a',marginTop:6}}>Scoré {timeAgo(w.last_score_at)}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
